import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ProductCard from "./ProductCard";
import Loader from "../../Loader/Loader";

const ProductList = () => {
    const productsData = useSelector((state) => state.product.productsData);
    const [searchParams] = useSearchParams();
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const minPrice = useMemo(() => parseFloat(searchParams.get("minPrice")) || 0, [searchParams]);
    const maxPrice = useMemo(() => parseFloat(searchParams.get("maxPrice")) || 150000, [searchParams]);
    const minAge = useMemo(() => parseFloat(searchParams.get("minAge")) || 0, [searchParams]);
    const maxAge = useMemo(() => parseFloat(searchParams.get("maxAge")) || 60, [searchParams]);
    const selectedAvailability = searchParams.get("availability") || "";
    const searchTerm = searchParams.get("search")?.toLowerCase() || "";

    const selectedCategories = useMemo(
        () => (searchParams.get("category") ? searchParams.get("category").split("&") : []),
        [searchParams]
    );

    const selectedGrades = useMemo(
        () => (searchParams.get("grades") ? searchParams.get("grades").split("&") : []),
        [searchParams]
    );

    const selectedBrands = useMemo(
        () => (searchParams.get("brands") ? searchParams.get("brands").split("&") : []),
        [searchParams]
    );
    const sortOption = useMemo(() => searchParams.get("sort") || "popularity", [searchParams]);

    const convertToDate = (dateTimeString) => new Date(dateTimeString.replace(" ", "T"));

    const filterProducts = useCallback(() => {
        if (!productsData || productsData.length === 0 || productsData === "Loading") return [];

        let filteredProduct = Object.values(productsData).filter((data) => {
            const isCategoryMatch = selectedCategories.length === 0 || selectedCategories.includes(data.category_slug);
            const isGradeMatch = selectedGrades.length === 0 || selectedGrades.some((grade) => data.grade_slugs.includes(grade));
            const isBrandMatch = selectedBrands.length === 0 || selectedBrands.includes(data.brand_slug);
            const isPriceMatch = data.sale_price >= minPrice && data.sale_price <= maxPrice;
            const isAgeMatch = data.min_age >= minAge || data.max_age <= maxAge;
            const isAvailabilityMatch = selectedAvailability.length === 0 || data.in_stock_quantity > 0;
            const isSearchTermMatch = data.name.toLowerCase().includes(searchTerm)
                || data.brand_name.toLowerCase().includes(searchTerm)
                || data.sub_category_name.toLowerCase().includes(searchTerm) ||data.category_name.toLowerCase().includes(searchTerm);

            return (
                isCategoryMatch &&
                isPriceMatch &&
                isAgeMatch &&
                isGradeMatch &&
                isAvailabilityMatch &&
                isBrandMatch &&
                isSearchTermMatch
            );
        });

        switch (sortOption) {
            case "price-asc":
                filteredProduct.sort((a, b) => a.sale_price - b.sale_price);
                break;
            case "price-desc":
                filteredProduct.sort((a, b) => b.sale_price - a.sale_price);
                break;
            case "rating":
                filteredProduct.sort((a, b) => b.ratings - a.ratings);
                break;
            case "popularity":
                filteredProduct.sort((a, b) => a.popularity - b.popularity);
                break;
            case "alphabetically":
                filteredProduct.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
                break;
            case "new_arrival":
                filteredProduct.sort((a, b) => convertToDate(b.created_at) - convertToDate(a.created_at));
                break;
            default:
                filteredProduct.sort((a, b) => a.popularity - b.popularity);
                break;
        }
        return filteredProduct;
    }, [productsData, selectedCategories, selectedGrades, minPrice, maxPrice, minAge, maxAge, selectedAvailability, selectedBrands, sortOption, searchTerm]);

    useEffect(() => {
        setIsLoading(true);
        const filtered = filterProducts();
        setFilteredProducts(filtered);
        productsData !== "Loading" && setIsLoading(false);
    }, [filterProducts, productsData]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="product-list">
            <div className="grid xl:grid-cols-5 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2">
                {filteredProducts.length > 0 ? (
                    filteredProducts.map((data) => <ProductCard key={data.id} productItems={data} />)
                ) : (
                    <p>No products available in this filter.</p>
                )}
            </div>
        </div>
    );
};

export default ProductList;
